import { defineStore } from "pinia";
import services from "@/services";
import useApi from "@/hooks/useApi";
import useInfo from './info'
import { ImageBoxType } from '@/components/workshop/Playground/ImageBox';
import utils from '@/utils';

const { getParam } = utils;

type SelectedSticker = {
  id: null | number;
  img: null | string;
}

export type PlaygroundSticker = {
  type: ImageBoxType;
  x: number;
  y: number;
  size: number;
  imageBoxId: number;
  selectedSticker: SelectedSticker;
};
type EventSticker = {
  basemaps: {
    image: string;
    positions: {
      x: number;
      y: number;
      size: number;
    }[];
    stickers: {
      id: number;
      image: string;
    }[];
  }[];
}

interface WorkshopState {
  eventSticker: EventSticker | null;
  eventEntrance: {
    image: string;
    name: string;
    description: string;
    eventLink: string;
    unlockTime: string;
  } | null;
  playgroundStickerMap: {
    [key: string | number]: PlaygroundSticker[];
  };
  stickerMap: {
    [key: string | number]: {
      id: number;
      image: string;
    }[];
  };
  basemapList: string[];
  selectedBasemapIndex: number;
  screenshotMode: boolean;
  waitScreenshot: boolean;
}

const useWorkshop = defineStore('workshop', {
  state: (): WorkshopState => ({
    eventSticker: {
      basemaps: []
    },
    eventEntrance: {
      image: '',
      name: '',
      description: '',
      eventLink: '',
      unlockTime: ''
    },
    playgroundStickerMap: {},
    stickerMap: {},
    basemapList: [],
    selectedBasemapIndex: 0,
    screenshotMode: false,
    waitScreenshot: false,
  }),
  actions: {
    async getWorkshop() {
      const { lang, region } = useInfo();
      if (!lang || !region) return;
      const response = await useApi(() => services.getWorkshop(
        {
          region,
          lang,
          ...(this.storageToken && { accessToken: this.storageToken })
        }));
      if (!response || response.msg) return;
      const { eventEntrance, eventSticker } = response.data
      this.eventEntrance = eventEntrance;
      this.eventSticker = eventSticker;
      this.initWorkshop();
    },
    initWorkshop() {
      let imageBoxId = 0;
      this.basemapList = [];
      this.eventSticker?.basemaps.forEach((item, index) => {
        this.basemapList.push(item.image);
        this.stickerMap[index] = item.stickers;
        this.playgroundStickerMap[index] = item.positions.map(position => ({
          type: ImageBoxType.Create,
          x: position.x,
          y: position.y,
          size: position.size,
          imageBoxId: imageBoxId++,
          selectedSticker: {
            id: null,
            img: null,
          }
        }))
      })
    },
    changeBasemap(index: number) {
      this.selectedBasemapIndex = index;
    },
    updateSelectedSticker<T extends Partial<PlaygroundSticker>>(sticker: T, index: number) {
      const originalStickerInfo = this.playgroundStickerMap[this.selectedBasemapIndex][index];
      this.playgroundStickerMap[this.selectedBasemapIndex][index] = {
        ...originalStickerInfo,
        ...sticker
      }
    },
    updateScreenshotMode(status: boolean) {
      if (this.waitScreenshot) return;
      this.screenshotMode = status;
    },
    updateWaitScreenshot(status: boolean) {
      this.waitScreenshot = status;
    }
  },
  getters: {
    currentBasemap(state) {
      return state.basemapList[state.selectedBasemapIndex];
    },
    currentStickerList(state) {
      return state.stickerMap[state.selectedBasemapIndex];
    },
    currentPlaygroundStickers(state) {
      return state.playgroundStickerMap[state.selectedBasemapIndex];
    },
    urlToken() {
      return getParam('access_token')
    },
    storageToken() {
      return localStorage.getItem('TOKEN')
    }
  }
});

export default useWorkshop;
